import {
  Divider,
  DropdownSubmitButton,
  Form,
  Loading,
  RadioGroupField,
  SelectField,
  SubmitButton,
  Text,
  TextAreaInputField,
} from '@/components';
import { getNameWithSortOrder } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import {
  type ActivityTreeNodeFormSchema,
  ActivityTreeNodeFormValidationSchema,
  IndicatorTypeLabelModifier,
  type TermType,
  TermTypeLabelModifier,
  TreeNodeTypeLabelModifier,
  treeNodeType,
} from '@/features/project/projects/_models/activity';
import {
  useActivityTreeEdges,
  useIndicators,
} from '@/features/project/projects/detail/activities/_hooks/activity';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { groupBy, orderBy } from 'lodash-es';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const getIndicatorOrder = (term: TermType | null) => {
  switch (term) {
    case 'short':
      return 2;
    case 'medium':
      return 3;
    case 'long':
      return 4;
    default:
      return 1;
  }
};

const OrderLabelModifier = {
  1: IndicatorTypeLabelModifier.output,
  2: `${TermTypeLabelModifier.short}${IndicatorTypeLabelModifier.outcome}`,
  3: `${TermTypeLabelModifier.medium}${IndicatorTypeLabelModifier.outcome}`,
  4: `${TermTypeLabelModifier.long}${IndicatorTypeLabelModifier.outcome}`,
} as const;

export type ActivityTreeNodeFormValues = z.infer<
  typeof ActivityTreeNodeFormSchema
>;

type CreateActivityTreeNodeNextDrawerKey =
  | 'activity-tree-node-form-next-target-form'
  | 'activity-tree-node-form-same-source-form'
  | 'activity-tree-node-form';

interface Option {
  label: string;
  value: string;
}

interface Props {
  activityOptions: Option[];
  onSubmit: ({
    values,
    nextDrawerKey,
  }: {
    values: ActivityTreeNodeFormValues;
    nextDrawerKey?: CreateActivityTreeNodeNextDrawerKey;
  }) => Promise<void>;
  initialValues?: ActivityTreeNodeFormValues;
  projectId: string;
  isEdit?: boolean;
}

const emptyValues: Partial<ActivityTreeNodeFormValues> = {
  nodeType: 'activity',
  sourceActivityId: undefined,
  sourceIndicatorId: undefined,
  targetIndicatorId: undefined,
  label: '',
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: '',
};

// TODO可読性が低いためアクティビティと活動・成果目標等でファイル分割をする

export const ActivityTreeNodeForm: FC<Props> = ({
  onSubmit,
  activityOptions,
  initialValues = emptyValues,
  projectId,
  isEdit,
}) => {
  const { t } = useTranslation();
  const activityTreeEdges = useActivityTreeEdges(projectId);

  const {
    handleSubmit,
    control,
    resetField,
    clearErrors,
    setValue,
    reset,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
  } = useForm<ActivityTreeNodeFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      ActivityTreeNodeFormValidationSchema.refine(
        (data) => {
          return isReasonRequired
            ? !!data.reasonWhyOutcomesCannotBeSetAtMultipleLevels
            : true;
        },
        {
          path: ['reasonWhyOutcomesCannotBeSetAtMultipleLevels'],
          message: t('validation.required'),
        },
      ).refine(
        (data) => {
          return isConnectionToNextOutcomeRequired ? !!data.label : true;
        },
        {
          path: ['label'],
          message: t('validation.required'),
        },
      ),
    ),
  });

  const [nodeType, sourceActivityId, sourceIndicatorId, targetIndicatorId] =
    watch([
      'nodeType',
      'sourceActivityId',
      'sourceIndicatorId',
      'targetIndicatorId',
    ]);

  const project = useProject(projectId);
  const indicators = useIndicators({
    filters: {
      fiscalYear: project.data?.fiscalYear.toString(),
      keyId: projectId,
    },
    select: (indicators) => {
      const indicatorsGroupByOrder = Object.entries(
        groupBy(
          indicators.map((indicator) => {
            return {
              order: getIndicatorOrder(indicator.term),
              ...indicator,
            };
          }),
          'order',
        ),
      );

      return orderBy(indicatorsGroupByOrder, (order) => order[0], 'asc').map(
        ([order, data]) => {
          return {
            label: OrderLabelModifier[order],
            type: data.at(0)?.type,
            term: data.at(0)?.term,
            options: orderBy(data, (d) => d.sortOrder, 'asc').map((d) => ({
              value: d.id,
              label: getNameWithSortOrder(d.sortOrder, d.title),
            })),
          };
        },
      );
    },
    enabled: Boolean(project.data?.fiscalYear),
  });
  const getTermFromLabel = (label: string) => {
    if (label.includes(TermTypeLabelModifier.short)) {
      return 'short';
    }
    if (label.includes(TermTypeLabelModifier.medium)) {
      return 'medium';
    }
    if (label.includes(TermTypeLabelModifier.long)) {
      return 'long';
    }
    return null;
  };

  const getIndicatorDetails = (
    indicatorId: string,
  ): {
    id: string;
    type: 'output' | 'outcome' | undefined;
    term: 'short' | 'medium' | 'long' | null;
  } | null => {
    if (!indicatorId || !indicators.data) {
      return null;
    }

    for (const group of indicators.data) {
      const indicator = group.options.find(
        (option) => option.value === indicatorId,
      );
      if (indicator) {
        return {
          id: indicator.value,
          type: group.type,
          term: getTermFromLabel(group.label),
        };
      }
    }
    return null;
  };

  const sourceIndicatorDetails = getIndicatorDetails(sourceIndicatorId);
  const targetIndicatorDetails = getIndicatorDetails(targetIndicatorId);

  const isConnectionToNextOutcomeRequired = useMemo<boolean>(() => {
    const sourceType = sourceIndicatorDetails?.type;
    const targetType = targetIndicatorDetails?.type;

    if (nodeType !== 'indicator') {
      return false;
    }

    if (sourceType === 'output') {
      return targetType === 'outcome';
    }

    if (targetType === 'output') {
      return false;
    }

    const sourceTerm = sourceIndicatorDetails?.term;
    const targetTerm = targetIndicatorDetails?.term;

    if (sourceTerm === 'short') {
      return targetTerm === 'medium' || targetTerm === 'long';
    }

    return targetTerm === 'long';
  }, [nodeType, sourceIndicatorDetails, targetIndicatorDetails]);

  const isDisabledWithUpperTermBySourceIndicator = useCallback(
    (term: 'short' | 'medium' | 'long' | null | undefined): boolean => {
      const sourceIndicatorTerm =
        nodeType === 'indicator' ? sourceIndicatorDetails?.term : undefined;
      if (!sourceIndicatorTerm || sourceIndicatorTerm === 'short') {
        return false;
      }
      switch (sourceIndicatorTerm) {
        case 'medium':
          return term === 'short';
        case 'long':
          return term === 'short' || term === 'medium';
      }
    },
    [sourceIndicatorDetails?.term, nodeType],
  );

  const duplicationRestrictedTargetIndicatorOptions = useMemo(() => {
    if (!indicators.data) {
      return [];
    }

    return indicators.data.flatMap((section) => {
      if (
        !isEdit &&
        ((nodeType === 'indicator' && section.type === 'output') ||
          (nodeType === 'activity' && section.type === 'outcome'))
      ) {
        return [];
      }

      return {
        ...section,
        options: section.options.map((indicator) => {
          const disabledWithUpperTermBySourceIndicator =
            isDisabledWithUpperTermBySourceIndicator(section.term);
          return {
            ...indicator,
            disabled:
              disabledWithUpperTermBySourceIndicator ||
              !!activityTreeEdges.data?.some(
                (edge) =>
                  edge.sourceNodeId === sourceIndicatorDetails?.id &&
                  edge.targetNode.indicatorId === indicator.value,
              ),
          };
        }),
      };
    });
  }, [
    indicators.data,
    nodeType,
    isDisabledWithUpperTermBySourceIndicator,
    activityTreeEdges.data,
    sourceIndicatorDetails?.id,
    isEdit,
  ]);

  if (indicators.isPending || !indicators.data) {
    return <Loading />;
  }

  const duplicationRestrictedActivityOptions = activityOptions.map(
    (activity) => {
      return {
        ...activity,
        disabled: activityTreeEdges.data?.some(
          (edge) =>
            edge.sourceNode.activityId === activity.value &&
            edge.targetNode.indicatorId === targetIndicatorId,
        ),
      };
    },
  );
  const duplicationRestrictedSourceIndicatorOptions = indicators.data.map(
    (section) => ({
      ...section,
      options: section.options.map((indicator) => {
        return {
          ...indicator,
          disabled: activityTreeEdges.data?.some(
            (edge) =>
              edge.sourceNode.indicatorId === indicator.value &&
              edge.targetNode.indicatorId === targetIndicatorId,
          ),
        };
      }),
    }),
  );

  const isReasonRequired =
    nodeType === 'indicator' &&
    sourceIndicatorDetails?.type === 'output' &&
    targetIndicatorDetails?.type === 'outcome' &&
    targetIndicatorDetails?.term === 'long';

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit((values) => {
        onSubmit({ values });
      })}
    >
      <Text variant="h4" text={t('source-node')} />
      <RadioGroupField
        name="nodeType"
        label={t('node-type')}
        control={control}
        errors={errors}
        disabled={isEdit}
        options={treeNodeType.options.map((value) => ({
          value,
          label: TreeNodeTypeLabelModifier[value],
        }))}
        shouldCoexistOnChange
        onChange={(e) => {
          const parsedNodeType = treeNodeType.parse(e.target.value);

          if (parsedNodeType === 'activity') {
            resetField('sourceIndicatorId', {
              keepDirty: false,
              keepError: false,
              keepTouched: false,
              defaultValue: initialValues.sourceIndicatorId ?? null,
            });
          }

          if (parsedNodeType === 'indicator') {
            resetField('sourceActivityId', {
              keepDirty: false,
              keepError: false,
              keepTouched: false,
              defaultValue: initialValues.sourceActivityId ?? null,
            });
          }
        }}
      />

      {nodeType === 'activity' && (
        <SelectField
          showSearch
          name="sourceActivityId"
          label={TreeNodeTypeLabelModifier.activity}
          control={control}
          errors={errors}
          options={duplicationRestrictedActivityOptions}
          disabled={isEdit}
          shouldCoexistOnChange
          onChange={() => {
            setValue('targetIndicatorId', null as any);
          }}
          required
        />
      )}

      {nodeType === 'indicator' && (
        <SelectField
          showSearch
          name="sourceIndicatorId"
          label={TreeNodeTypeLabelModifier.indicator}
          control={control}
          errors={errors}
          options={duplicationRestrictedSourceIndicatorOptions}
          loading={indicators.isPending}
          disabled={isEdit}
          required
          shouldCoexistOnChange
          onChange={() => {
            setValue('targetIndicatorId', null as any);
            clearErrors('label');
          }}
        />
      )}

      <Text variant="h4" text={t('target-node')} />
      <SelectField
        showSearch
        name="targetIndicatorId"
        label={TreeNodeTypeLabelModifier.indicator}
        control={control}
        errors={errors}
        options={duplicationRestrictedTargetIndicatorOptions}
        loading={indicators.isPending}
        disabled={
          isEdit ||
          (nodeType === 'activity' && !sourceActivityId) ||
          (nodeType === 'indicator' && !sourceIndicatorId)
        }
        required
        shouldCoexistOnChange
        onChange={() => {
          clearErrors('label');
        }}
      />

      {nodeType === 'indicator' && (
        <TextAreaInputField
          name="label"
          label={t('connection-to-next-outcome')}
          control={control}
          errors={errors}
          rows={3}
          required={isConnectionToNextOutcomeRequired}
        />
      )}

      <Divider />

      <TextAreaInputField
        name="reasonWhyOutcomesCannotBeSetAtMultipleLevels"
        control={control}
        errors={errors}
        label={t('reason-why-outcomes-cannot-be-multiple')}
        hint={t('hint-reason-why-outcomes-cannot-be-multiple')}
        rows={3}
        required={isReasonRequired}
      />

      {isEdit ? (
        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      ) : (
        <DropdownSubmitButton<CreateActivityTreeNodeNextDrawerKey>
          loading={isSubmitting}
          menuItems={[
            {
              key: 'activity-tree-node-form-next-target-form',
              label: t('save-and-create-from-to-connection', {
                from: t('target-node'),
                to: t('next-target-node'),
              }),
            },
            {
              key: 'activity-tree-node-form-same-source-form',
              label: t('save-and-create-from-to-connection', {
                from: t('same-source-node'),
                to: t('another-target-node'),
              }),
            },
            {
              key: 'activity-tree-node-form',
              label: t('save-and-create-item', {
                item: t('another-connection'),
              }),
            },
          ]}
          onClick={(e) => {
            handleSubmit(async (values) => {
              const nextDrawerKey = e.key;
              await onSubmit({
                values,
                nextDrawerKey,
              });
              if (
                nextDrawerKey === 'activity-tree-node-form-next-target-form'
              ) {
                reset({
                  ...emptyValues,
                  nodeType: 'indicator',
                  sourceActivityId: undefined,
                  sourceIndicatorId: values.targetIndicatorId,
                  targetIndicatorId: '',
                });
              }
              if (
                nextDrawerKey === 'activity-tree-node-form-same-source-form'
              ) {
                reset({
                  ...emptyValues,
                  nodeType: values.nodeType,
                  sourceActivityId:
                    values.nodeType === 'activity'
                      ? values.sourceActivityId
                      : undefined,
                  sourceIndicatorId:
                    values.nodeType === 'indicator'
                      ? values.sourceIndicatorId
                      : undefined,
                  targetIndicatorId: '',
                });
              }
              if (nextDrawerKey === 'activity-tree-node-form') {
                reset(emptyValues);
              }
            })();
          }}
        />
      )}
    </Form>
  );
};
